/**
 * Structured Logger
 *
 * Centralized logging via pino. Replaces raw console.log/error/warn
 * with structured JSON logs that include context, timestamps, and log levels.
 *
 * Usage:
 *   import { logger } from '@/lib/logger'
 *
 *   // Basic logging
 *   logger.info('Server started')
 *   logger.error({ err, userId }, 'Failed to fetch reservations')
 *
 *   // Child loggers for specific modules
 *   const log = logger.child({ module: 'helcim' })
 *   log.info({ orderId }, 'Card charged successfully')
 *
 *   // API route helper
 *   const log = apiLogger('POST /api/flight/charges/bill')
 *   log.info({ reservationId }, 'Processing billing request')
 *   log.error({ err }, 'Billing failed')
 *
 * Log levels (in order):
 *   trace → debug → info → warn → error → fatal
 *
 * In production: JSON output (machine-parseable for log aggregation)
 * In development: pretty-printed with colors via pino-pretty
 */

import pino from 'pino'

const isProduction = process.env.NODE_ENV === 'production'
const isTest = process.env.NODE_ENV === 'test'

// Pretty-print logs in the Node.js dev server, while keeping pino-pretty out of
// the Edge/middleware and client bundles.
//
// Why an in-process stream instead of pino's `transport` option: `transport`
// runs pino-pretty in a separate worker thread (via thread-stream). Next.js HMR
// tears down and recompiles the module graph on every edit, killing that worker
// while the `logger` singleton keeps a reference to the now-dead thread-stream.
// The next log call then throws `Error: the worker has exited` as an
// uncaughtException. A synchronous in-process stream has no worker to die.
//
// Why a gated require instead of a static import: logger.ts is reachable from
// middleware (Edge runtime) via auth/server -> auth/config. A static
// `import 'pino-pretty'` forces webpack to bundle pino-pretty's node:* deps
// (worker_threads, stream) into the Edge/client bundles, which fail to compile.
// Next.js replaces `process.env.NEXT_RUNTIME` at build time per runtime, so this
// branch — and the require inside it — is dead-code-eliminated from every bundle
// except the Node.js server. pino-pretty is also listed in
// `serverExternalPackages` (next.config.ts) so the Node server requires it at
// runtime rather than webpack bundling its node:* deps.
//
// Production/test keep pino's default async stdout (JSON) output, unchanged.
function createDevStream() {
  if (isProduction || isTest) return undefined
  // In the Edge/client builds NEXT_RUNTIME is statically 'edge'/undefined here,
  // so webpack prunes this whole block. In the Node server it is 'nodejs'; in a
  // plain Node process (e.g. scripts) it is undefined.
  if (process.env.NEXT_RUNTIME === 'nodejs' || process.env.NEXT_RUNTIME === undefined) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const pretty = require('pino-pretty')
      return pretty({
        colorize: true,
        translateTime: 'HH:MM:ss',
        ignore: 'pid,hostname',
        sync: true,
      })
    } catch {
      // pino-pretty unavailable — fall back to default stdout below.
      return undefined
    }
  }
  return undefined
}

const devStream = createDevStream()

export const logger = pino(
  {
    level: isTest ? 'silent' : (process.env.LOG_LEVEL || (isProduction ? 'info' : 'debug')),

    // Redact sensitive fields from log output
    redact: {
      paths: [
        'password',
        'temp_password',
        'card_token',
        'helcim_customer_code',
        'api_key',
        'apiKey',
        'authorization',
        'cookie',
        'SUPABASE_SERVICE_ROLE_KEY',
        'HELCIM_API_TOKEN',
        'ANTHROPIC_API_KEY',
        'RESEND_API_KEY',
        '*.password',
        '*.temp_password',
        '*.card_token',
        '*.api_key',
        'headers.authorization',
        'headers.cookie',
      ],
      censor: '[REDACTED]',
    },

    // Serialize Error objects properly
    serializers: {
      err: pino.stdSerializers.err,
      error: pino.stdSerializers.err,
    },
  },
  // Development: in-process synchronous pretty stream (no worker thread, HMR-safe).
  // Production/test: undefined → pino's default async stdout (JSON), unchanged.
  devStream
)

/**
 * Creates a child logger scoped to a specific API route.
 * Automatically includes the route path in every log entry.
 *
 * @example
 *   const log = apiLogger('POST /api/flight/charges/bill')
 *   log.info({ orderId: '123' }, 'Processing charge')
 */
export function apiLogger(route: string) {
  return logger.child({ route })
}

/**
 * Creates a child logger scoped to a specific module/service.
 *
 * @example
 *   const log = moduleLogger('helcim')
 *   log.info({ transactionId }, 'Payment processed')
 */
export function moduleLogger(module: string) {
  return logger.child({ module })
}
